import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { FileText, Search, Download, Filter } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { formatCategoryLabel } from '../utils/normalizeAcronyms';
import { toValidDate } from '../utils/formatDate';

interface AuditLogEntry {
  id?: string; // entries written as `audit:<kind>:<user>:<time>` carry no id of their own
  timestamp?: string; // older message and AI-settings entries were written without one
  userId: string;
  userEmail: string;
  userName?: string;
  companyId?: string;
  companyName?: string;
  action: string;
  entityType?: string;
  entityId?: string;
  entityName?: string;
  details?: any;
}

interface AuditLogProps {
  accessToken: string;
  companyId?: string; // If provided, only show logs for this company
  /** If provided (and no companyId), the firm's trail: its companies and its members (Firm › Audit). */
  firmId?: string;
  isSystemAdmin?: boolean;
  maxResults?: number; // Limit number of results for performance
}

export function AuditLog({ accessToken, companyId, firmId, isSystemAdmin, maxResults = 500 }: AuditLogProps) {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [entityFilter, setEntityFilter] = useState<string>('all');

  useEffect(() => {
    loadAuditLogs();
  }, [companyId, firmId]);

  useEffect(() => {
    // Apply filters
    let filtered = logs;

    if (searchTerm) {
      filtered = filtered.filter(log =>
        log.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.entityName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.companyName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (actionFilter !== 'all') {
      filtered = filtered.filter(log => log.action === actionFilter);
    }

    if (entityFilter !== 'all') {
      filtered = filtered.filter(log => log.entityType === entityFilter);
    }

    setFilteredLogs(filtered);
  }, [logs, searchTerm, actionFilter, entityFilter]);

  const loadAuditLogs = async () => {
    console.log('[FRONTEND] Starting to load audit logs...');
    const startTime = Date.now();
    setLoading(true);
    try {
      const url = companyId
        ? `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/audit/company/${companyId}?limit=${maxResults}`
        : firmId
          ? `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/audit?limit=${maxResults}`
          : `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/audit/all?limit=${maxResults}`;

      console.log(`[FRONTEND] Fetching audit logs from: ${url}`);
      const fetchStart = Date.now();
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      console.log(`[FRONTEND] Audit logs fetch completed in ${Date.now() - fetchStart}ms, status: ${response.status}`);

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setFilteredLogs(data.logs || []);
        console.log(`[FRONTEND] Audit logs loaded: ${data.logs?.length || 0} entries in ${Date.now() - startTime}ms`);
      } else {
        console.error('Failed to load audit logs:', response.status, response.statusText);
      }
    } catch (err) {
      console.error('[FRONTEND] Error loading audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const getActionBadge = (action: string) => {
    const actionConfig: Record<string, { variant: any; label: string }> = {
      'created': { variant: 'default', label: 'Created' },
      'updated': { variant: 'secondary', label: 'Updated' },
      'deleted': { variant: 'destructive', label: 'Deleted' },
      'marked_ready': { variant: 'default', label: 'Marked Ready' },
      'reopened': { variant: 'outline', label: 'Reopened' },
      'marked_implemented': { variant: 'default', label: 'Implemented' },
      'role_changed': { variant: 'secondary', label: 'Role Changed' },
      'permission_changed': { variant: 'secondary', label: 'Permission Changed' },
      'user_created': { variant: 'default', label: 'User Created' },
      'user_invited': { variant: 'default', label: 'User Invited' },
      'user_removed': { variant: 'destructive', label: 'User Removed' },
      'data_purged': { variant: 'destructive', label: 'Data Purged' },
      'company_created': { variant: 'default', label: 'Company Created' },
    };

    const config = actionConfig[action] || { variant: 'outline', label: action };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  // Intl.DateTimeFormat throws "Invalid time value" on an invalid Date, which
  // took down Firm › Audit on staging (2026-10-29) over one entry with no time.
  const formatDate = (timestamp: string | undefined) => {
    const date = toValidDate(timestamp);
    if (!date) return 'No time recorded';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(date);
  };

  const exportToCSV = () => {
    const headers = ['Timestamp', 'User', 'Email', 'Company', 'Action', 'Entity Type', 'Entity Name', 'Details'];
    const rows = filteredLogs.map(log => [
      log.timestamp,
      log.userName || log.userEmail,
      log.userEmail,
      log.companyName || '',
      log.action,
      log.entityType || '',
      log.entityName || '',
      JSON.stringify(log.details || {}),
    ]);

    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString()}.csv`;
    a.click();
  };

  const uniqueActions = Array.from(new Set(logs.map(log => log.action)));
  const uniqueEntityTypes = Array.from(new Set(logs.map(log => log.entityType).filter(Boolean)));

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Loading audit logs...
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Audit Log
              {companyId && !isSystemAdmin && ' - Company Activity'}
            </CardTitle>
            <CardDescription>
              {isSystemAdmin 
                ? 'Recorded audit events across all tenants'
                : 'Activity log for this company'}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={exportToCSV}>
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={loadAuditLogs}>
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Input
                placeholder="Search by user, entity, or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-sm"
              />
            </div>
            <div className="w-48">
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {uniqueActions.map(action => (
                    <SelectItem key={action} value={action}>
                      {formatCategoryLabel(action)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Select value={entityFilter} onValueChange={setEntityFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Entities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  {uniqueEntityTypes.map(type => (
                    <SelectItem key={type} value={type!}>
                      {formatCategoryLabel(type!)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Results count */}
          <div className="flex items-center justify-between">
            <div className="text-muted-foreground">
              Showing {filteredLogs.length} of {logs.length} entries
              {logs.length >= maxResults && (
                <span className="ml-2 text-yellow-600">
                  (limited to most recent {maxResults} for performance)
                </span>
              )}
            </div>
          </div>

          {/* Audit log table */}
          <ScrollArea className="h-[600px] rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  {isSystemAdmin && <TableHead>Company</TableHead>}
                  <TableHead>Action</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isSystemAdmin ? 6 : 5} className="text-center text-muted-foreground py-8">
                      No audit log entries found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLogs.map((log, index) => (
                    <TableRow key={log.id || `${log.action}-${log.timestamp || 'no-time'}-${index}`}>
                      <TableCell className="whitespace-nowrap">
                        {formatDate(log.timestamp)}
                      </TableCell>
                      <TableCell>
                        <div>
                          <div>{log.userName || log.userEmail}</div>
                          {log.userName && (
                            <div className="text-muted-foreground">{log.userEmail}</div>
                          )}
                        </div>
                      </TableCell>
                      {isSystemAdmin && (
                        <TableCell>{log.companyName || '-'}</TableCell>
                      )}
                      <TableCell>{getActionBadge(log.action)}</TableCell>
                      <TableCell>
                        {log.entityType && (
                          <div>
                            <div className="capitalize">{formatCategoryLabel(log.entityType)}</div>
                            {log.entityName && (
                              <div className="text-muted-foreground">{log.entityName}</div>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {log.details && typeof log.details === 'object' 
                          ? Object.entries(log.details)
                              .map(([key, value]) => `${key}: ${value}`)
                              .join(', ')
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>
      </CardContent>
    </Card>
  );
}